import React, {useState, useEffect } from 'react';
import {useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {doc, getDoc } from 'firebase/firestore';
// CORRECTED IMPORT: Import firebaseAppId directly from your firebase.ts
import {db, firebaseAppId } from '../../firebase';
import {Timestamp } from 'firebase/firestore';
import {
  MainLayout,
  LoadingMessage,
  ErrorMessage,
  SoldBadge,
} from './styles/home.styles';
import {theme } from '../../styles/theme';

// Re-define MediaItem interface (must be consistent across components)
interface MediaItem {
  url: string;
  type: 'image' | 'video';
}

// Update Listing interface to use `media: MediaItem[]`
interface Listing {
  id: string;
  title: string;
  description: string;
  category: string;
  price: number;
  location: string;
  contact: string;
  sold: boolean;
  media: MediaItem[]; // CHANGED: Now uses MediaItem[]
  userId: string;
  createdAt?: Date;
  // Add any other specific fields you might have in your Firestore documents
  amenities?: string[];
  bedrooms?: number;
  bathrooms?: number;
  areaSqFt?: number;
  // ... more detailed fields
}

// Styled components for the detail page (mostly unchanged, except adding CarouselVideo)
const DetailContainer = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} auto;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.md};
    margin: ${theme.spacing.md} auto;
  }
`;

const DetailHeader = styled.div`
  text-align: center;
  border-bottom: 2px solid ${theme.colors.borderLight};
  padding-bottom: ${theme.spacing.md};
`;

const DetailTitle = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.dark};
  margin-bottom: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: 2rem;
  }
`;

const DetailPrice = styled.p`
  font-size: 1.8rem;
  font-weight: bold;
  color: ${theme.colors.primaryDark};
  margin-bottom: ${theme.spacing.md};
`;

const DetailImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  max-height: 400px;
  overflow-y: auto;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: 8px;

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
`;

const GalleryThumbnail = styled.img<{isActive: boolean }>`
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid ${({isActive }) => isActive ? theme.colors.primary : 'transparent'};
  transition: border 0.2s ease, transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const MainMediaContainer = styled.div` // Changed from MainImageContainer
  width: 100%;
  height: 450px;
  background-color: ${theme.colors.background};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  display: flex; /* Ensure content is centered if needed */
  justify-content: center;
  align-items: center;

  @media (max-width: ${theme.breakpoints.md}) {
    height: 300px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer; /* Enable click for potential zoom */
`;

const MainVideo = styled.video` // NEW: Styled component for video
  width: 100%;
  height: 100%;
  object-fit: cover;
`;


const DetailSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: ${theme.colors.dark};
  border-bottom: 1px solid ${theme.colors.border};
  padding-bottom: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const DetailText = styled.p`
  font-size: 1rem;
  line-height: ${theme.typography.lineHeight};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const DetailMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`;

const MetaItem = styled.div`
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.sm};
  border-radius: 6px;
  font-size: 0.9rem;
  color: ${theme.colors.textDark};
  strong {
    color: ${theme.colors.dark};
    display: block;
    margin-bottom: ${theme.spacing.xs};
  }
`;

const ContactButton = styled.a`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1rem;
  text-align: center;
  margin-top: ${theme.spacing.lg};
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: ${theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid ${theme.colors.textLight};
  color: ${theme.colors.text};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: 8px;
  cursor: pointer;
  margin-top: ${theme.spacing.md};
  font-size: 0.9rem;
  align-self: flex-start;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.background};
    color: ${theme.colors.dark};
    border-color: ${theme.colors.dark};
  }
`;

export const ListingDetail = () => {
  const {id } = useParams<{id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMainMediaUrl, setCurrentMainMediaUrl] = useState<string | undefined>(undefined);

  // Helper to infer media type (copied from Home.tsx for consistency in detail view)
  const inferMediaType = (url: string): 'image' | 'video' => {
    const decodedUrl = decodeURIComponent(url);
    const lowerCaseDecodedUrl = decodedUrl.toLowerCase();
    if (
      lowerCaseDecodedUrl.endsWith('.mp4') ||
      lowerCaseDecodedUrl.endsWith('.webm') ||
      lowerCaseDecodedUrl.endsWith('.ogg') ||
      lowerCaseDecodedUrl.endsWith('.mov')
    ) {
      return 'video';
    }
    return 'image';
  };

  useEffect(() => {
    const fetchListing = async () => {
      if (!id) {
        setError("No listing ID provided.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        console.log('Debug: firebaseAppId (imported):', firebaseAppId); 

        let docRef;
        let docSnap;

        if (firebaseAppId) {
            docRef = doc(db, `artifacts/${firebaseAppId}/estates`, id);
            docSnap = await getDoc(docRef);
        }

        if (!docSnap || !docSnap.exists()) {
          docRef = doc(db, "listings", id);
          docSnap = await getDoc(docRef);
        }

        if (docSnap.exists()) {
          const data = docSnap.data();

          // Initialize media array for the Listing interface
          let listingMedia: MediaItem[] = [];

          if (Array.isArray(data.media)) {
              data.media.forEach((item: any) => {
                  if (typeof item.url === 'string' && (item.type === 'image' || item.type === 'video')) {
                      listingMedia.push({url: item.url, type: item.type });
                  }
              });
          }
          // FALLBACK 1: Check for `images` array (from older/different general listings, or if estates stored them simply)
          else if (Array.isArray(data.images) && data.images.every((img: any) => typeof img === 'string')) {
              data.images.forEach((url: string) => {
                  listingMedia.push({url, type: inferMediaType(url) });
              });
          }
          // FALLBACK 2: Check for a single `imageUrl` string (from Estate type)
          else if (typeof data.imageUrl === 'string') {
              listingMedia.push({url: data.imageUrl, type: inferMediaType(data.imageUrl) });
          }

          let createdAtDate: Date | undefined;
          if (data.createdAt instanceof Timestamp) {
            createdAtDate = data.createdAt.toDate();
          } else if (data.createdAt && typeof data.createdAt.toDate === 'function') {// More robust check
            createdAtDate = data.createdAt.toDate();
          } else if (data.createdAt instanceof Date) {
            createdAtDate = data.createdAt;
          }

          const fetchedListing: Listing = {
            id: docSnap.id,
            title: data.name || data.title || 'Untitled Listing',
            description: data.description || 'No description available.',
            category: data.type || data.category || 'N/A',
            price: data.price || 0,
            location: data.location || 'Unknown',
            contact: data.contact || 'Not provided',
            sold: data.status === 'sold' || data.sold === true,
            media: listingMedia, // Assign the processed MediaItem[]
            userId: data.userId || 'N/A',
            createdAt: createdAtDate,
            amenities: data.amenities || [],
            bedrooms: data.bedrooms,
            bathrooms: data.bathrooms,
            areaSqFt: data.areaSqFt,
          };
          setListing(fetchedListing);
          // Set the first media item as the current main display
          if (fetchedListing.media.length > 0) {
            setCurrentMainMediaUrl(fetchedListing.media[0].url);
          } else {
            setCurrentMainMediaUrl(`https://placehold.co/800x450/cccccc/333333?text=No+Media`);
          }
          setError(null);
        } else {
          setError("Listing not found.");
        }
      } catch (err: any) {
        console.error("Error fetching listing:", err);
        setError(`Failed to load listing: ${err.message}.`);
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id]);

  if (loading) {
    return <MainLayout><LoadingMessage>Loading listing details...</LoadingMessage></MainLayout>;
  }

  if (error) {
    return <MainLayout><ErrorMessage>Error: {error}</ErrorMessage></MainLayout>;
  }

  if (!listing) {
    return <MainLayout><ErrorMessage>Listing data is unavailable.</ErrorMessage></MainLayout>;
  }

  const defaultPlaceholderMediaUrl = `https://placehold.co/800x450/cccccc/333333?text=No+Media`;
  const currentMainMediaItem = listing.media.find(item => item.url === currentMainMediaUrl) ||
                                (listing.media.length > 0 ? listing.media[0] : null); // Fallback to first if URL not found
  const displayMainMediaUrl = currentMainMediaItem ? currentMainMediaItem.url : defaultPlaceholderMediaUrl;
  const displayMainMediaType = currentMainMediaItem ? currentMainMediaItem.type : 'image'; // Assume image for placeholder


  return (
    <MainLayout>
      <DetailContainer>
        <BackButton onClick={() => navigate(-1)}>← Back to Listings</BackButton>

        <DetailHeader>
          <DetailTitle>{listing.title}</DetailTitle>
          <DetailPrice>Ugx: {listing.price.toLocaleString()}</DetailPrice>
          {listing.sold && <SoldBadge>SOLD</SoldBadge>}
        </DetailHeader>

        {/* Main Media Display (Image or Video) */}
        <MainMediaContainer> {/* Changed from MainImageContainer */}
          {displayMainMediaType === 'image' ? (
            <MainImage
              src={displayMainMediaUrl}
              alt={listing.title}
              onError={(e) => {
                (e.target as HTMLImageElement).onerror = null;
                (e.target as HTMLImageElement).src = defaultPlaceholderMediaUrl;
              }}
              // Implement zoom functionality here (e.g., open a modal with a larger image)
              onClick={() => {/* Open image in a lightbox/modal */ console.log("Zoom image clicked:", displayMainMediaUrl); }}
            />
          ) : (
            <MainVideo // Use the new styled video component
              src={displayMainMediaUrl}
              controls // Allows user to play/pause, seek, volume etc.
              preload="metadata" // Loads just enough to display first frame
              playsInline // Important for iOS to play video inline, not fullscreen automatically
              // autoPlay
              // loop
              onError={(e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
                console.error(`Video failed to load or play for URL: ${displayMainMediaUrl}`, e);
                // Optionally set a fallback image or message here
              }}
            >
              Your browser does not support the video tag.
            </MainVideo>
          )}
        </MainMediaContainer>

        {/* Gallery Thumbnails */}
        {listing.media && listing.media.length > 0 && ( // Iterate over listing.media
          <DetailImageGallery>
            {listing.media.map((item, index) => (
              <GalleryThumbnail
                key={index}
                // Show actual image, or a generic video icon/thumbnail for videos
                src={item.type === 'image' ? item.url : `https://placehold.co/100x100/3498db/ffffff?text=Video`}
                alt={`${listing.title} thumbnail ${index + 1}`}
                isActive={item.url === currentMainMediaUrl}
                onClick={() => setCurrentMainMediaUrl(item.url)} // Set the URL for main display
                onError={(e) => {
                  (e.target as HTMLImageElement).onerror = null;
                  (e.target as HTMLImageElement).src = defaultPlaceholderMediaUrl;
                }}
              />
            ))}
          </DetailImageGallery>
        )}

        {/* Description, Key Details, Amenities, Contact Button (no changes here) */}
        <DetailSection>
          <SectionTitle>Description</SectionTitle>
          <DetailText>{listing.description}</DetailText>
        </DetailSection>

        <DetailSection>
          <SectionTitle>Key Details</SectionTitle>
          <DetailMetaGrid>
            <MetaItem><strong>Category:</strong> {listing.category}</MetaItem>
            <MetaItem><strong>Location:</strong> {listing.location}</MetaItem>
            {listing.bedrooms && <MetaItem><strong>Bedrooms:</strong> {listing.bedrooms}</MetaItem>}
            {listing.bathrooms && <MetaItem><strong>Bathrooms:</strong> {listing.bathrooms}</MetaItem>}
            {listing.areaSqFt && <MetaItem><strong>Area:</strong> {listing.areaSqFt.toLocaleString()} sq ft</MetaItem>}
            <MetaItem><strong>Posted:</strong> {listing.createdAt ? listing.createdAt.toLocaleDateString() : 'N/A'}</MetaItem>
          </DetailMetaGrid>
        </DetailSection>

        {listing.amenities && listing.amenities.length > 0 && (
          <DetailSection>
            <SectionTitle>Amenities</SectionTitle>
            <ul>
              {listing.amenities.map((amenity, index) => (
                <li key={index}><DetailText>{amenity}</DetailText></li>
              ))}
            </ul>
          </DetailSection>
        )}

        <ContactButton href={`tel:${listing.contact}`}>
          Contact Seller: {listing.contact}
        </ContactButton>

      </DetailContainer>
    </MainLayout>
  );
};